import { emitter } from '@/main'
import { isGokollab } from '@ghl/clientportal-core/init'
import { defineStore } from 'pinia'
export interface AppStore {
  locationId: string
  groupId?: string
  userId: string
  isAuthenticated?: boolean
  showLoginModal?: boolean
  loginPage?: string
}

export const useGlobalStore = defineStore('global', {
  state: () => ({
    isGokollab: isGokollab(),
    locationId: '',
    groupId: '',
    userId: '',
    isAuthenticated: undefined as boolean | undefined,
    userEmail: '',
    token: '',
    appType: 'STANDALONE',
    companyId: '',
    timezone: '',
    redirectUrl: '',
    languagePreferenceBuilder: '',
    chatWidgetSettings: {
      widgetId: '',
      permissions: {
        communities: false,
        membership: false,
        affiliate: false,
        home: false,
        settings: false,
      },
    },
    requestRetryCount: 0,
    showLoginModal: false,
    loginPage: 'LOGIN',
    locationPermissions: {
      membershipEnabled: false,
      affiliateManagerEnabled: false,
      communitiesEnabled: false,
      gokollabEnabled: false,
    },
    branding: {
      brandColor2: null,
      brandColor1: null,
      portalBannerText: null,
      portalDescription: null,
      portalBackgroundImage: null,
      portalIcon: null,
      favicon: null,
      copyRight: null,
      supportEmail: null,
    },
    profileDetails: {
      email: '',
      fullName: '',
      bio: '',
      avatar: '',
      role: '',
      timeZone: '',
      location: '',
      slug: '',
      socials: {
        phoneNumber: '',
        facebook: '',
        twitter: '',
        instagram: '',
        linkedin: '',
        youtube: '',
        email: '',
      },
    },
    calendar: {
      isCalendarView: true,
      currentDate: new Date(),
    },
    isAppLoaded: false,
  }),
  getters: {
    isAuthResolved: state => state.isAuthenticated !== undefined,
    shouldShowLoginBanner: state =>
      state.isAppLoaded && state.isAuthenticated === false,
  },
  actions: {
    updateRequestRetryCount(count: number) {
      this.requestRetryCount = count
    },
    initialize(payload: any) {
      this.locationId = payload.locationId
      this.userId = payload.userId
      this.userEmail = payload.userEmail
      this.token = payload.token
      this.companyId = payload.companyId
      this.timezone = payload.timezone
      this.appType = payload.appType
      this.locationId = payload.locationId
      this.userId = payload.userId
      this.branding = {
        brandColor1: null,
        brandColor2: null,
        portalBannerText: null,
        portalIcon: null,
        portalDescription: null,
        portalBackgroundImage: null,
        favicon: null,
        copyRight: null,
        supportEmail: null,
      }
    },
    refreshToken(newToken: string) {
      this.token = newToken
    },

    setBrandingValue(value: any) {
      this.branding.portalBannerText = value.portalBannerText
      this.branding.portalDescription = value.portalDescription
      this.branding.brandColor1 = value.brandColor1
      this.branding.brandColor2 = value.brandColor2
      this.branding.portalIcon = value.portalIcon
      this.branding.favicon = value.favicon
      this.branding.copyRight = value.copyRight
      this.branding.supportEmail = value.supportEmail
      this.locationId = value.locationId
      this.branding.portalBackgroundImage = value.portalBackgroundImage
    },

    setProfileDetails(value: any) {
      this.userId = value.contactId
      this.profileDetails.email = value.email
      this.profileDetails.fullName = value.fullName
      this.profileDetails.bio = value?.bio
      this.profileDetails.avatar = value?.avatar
      this.profileDetails.timeZone = value?.timeZone
      this.profileDetails.location = value?.location
      this.profileDetails.slug = value?.slug
      this.profileDetails.socials.phoneNumber = value?.socials?.phoneNumber
      this.profileDetails.socials.facebook = value?.socials?.facebook
      this.profileDetails.socials.twitter = value?.socials?.twitter
      this.profileDetails.socials.instagram = value?.socials?.instagram
      this.profileDetails.socials.youtube = value?.socials?.youtube
      this.profileDetails.socials.linkedin = value?.socials?.linkedin
      this.profileDetails.socials.email = value?.socials?.email
    },
    setPermissions(payload) {
      this.locationPermissions.membershipEnabled =
        payload.locationPermissions?.membershipEnabled
      this.locationPermissions.affiliateManagerEnabled =
        payload.locationPermissions?.affiliateManagerEnabled
      this.locationPermissions.communitiesEnabled =
        payload.locationPermissions?.communitiesEnabled
      this.locationPermissions.gokollabEnabled =
        payload.locationPermissions?.gokollabEnabled
    },
    setChatWidgetPermissions(payload) {
      this.chatWidgetSettings.permissions.communities =
        payload.chatWidgetSettings?.permissions?.communities
      this.chatWidgetSettings.permissions.membership =
        payload.chatWidgetSettings?.permissions?.membership
      this.chatWidgetSettings.permissions.affiliate =
        payload.chatWidgetSettings?.permissions?.affiliate
      this.chatWidgetSettings.permissions.home =
        payload.chatWidgetSettings?.permissions?.home
    },

    setLanguagePreference(value: string) {
      this.languagePreferenceBuilder = value
    },

    setChatWidgetId(value: string) {
      this.chatWidgetSettings.widgetId = value
    },

    setRedirectUrl(redirectUrl: string) {
      this.redirectUrl = redirectUrl
    },
    setAuthenticated(value: boolean | undefined) {
      this.isAuthenticated = value
    },
    setLocationId(locationId: string) {
      this.locationId = locationId
    },
    setGroupId(groupId: string) {
      // console.log('setGroupId :: ', groupId)
      this.groupId = groupId
    },
    setUserId(userId: string) {
      this.userId = userId
    },
    setIsCalendarView(isCalendarView: boolean) {
      this.calendar.isCalendarView = isCalendarView
    },
    setAppLoaded(value: boolean) {
      this.isAppLoaded = value
      if (value) {
        emitter.emit('app:loaded')
      }
    },
    setShowLoginModal(show: boolean) {
      this.showLoginModal = show
    },
    setLoginPage(page: string) {
      this.loginPage = page
    },
    openLoginModal(page: string = 'LOGIN') {
      this.showLoginModal = true
      this.loginPage = page
    },
    closeLoginModal() {
      this.showLoginModal = false
    },
  },
})
